"use client"

import { useSearchParams } from "next/navigation"
import { Search, MessageCircle } from "lucide-react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { GridSkeleton, ListSkeleton } from "@/components/public/CatalogSkeletons"
import { useCatalogTransition } from "@/components/public/CatalogTransitionContext"
import { useTranslations } from "@/lib/i18n/client"

interface CatalogContentProps {
  /** Cards já renderizados server-side (grid ou lista, conforme ?view=). */
  children: React.ReactNode
  isEmpty: boolean
  whatsappNumber: string
}

function EmptyState({ whatsappNumber }: { whatsappNumber: string }) {
  const { t } = useTranslations()
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-[0.5px] border-dashed border-slate-300 bg-slate-50 py-20">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border-[0.5px] border-slate-200 bg-white">
        <Search className="h-5 w-5 text-slate-500" />
      </div>
      <p className="mt-4 font-heading text-lg font-medium text-navy-900">
        {t.catalog.emptyTitle}
      </p>
      <p className="mt-2 max-w-[420px] text-center text-sm text-slate-500">
        {t.catalog.emptyBody}
      </p>
      <div className="mt-5 flex items-center gap-2.5">
        <Button asChild variant="outline">
          <Link href="/aeronaves">{t.catalog.clearFilters}</Link>
        </Button>
        <a
          href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(t.catalog.emptyWhatsappMessage)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-navy-800 px-4 text-sm font-medium text-white transition-colors hover:bg-navy-900"
        >
          <MessageCircle className="h-4 w-4" />
          {t.catalog.emptyTalk}
        </a>
      </div>
    </div>
  )
}

/**
 * Wrapper client fino: mostra skeleton durante transições de filtro e o
 * empty state; os cards em si são server components passados como children
 * (sem custo de hidratação por tile).
 */
export function CatalogContent({ children, isEmpty, whatsappNumber }: CatalogContentProps) {
  const { isPending, pendingView } = useCatalogTransition()
  const searchParams = useSearchParams()
  const viewMode = searchParams.get("view") ?? "grid"

  if (isPending) {
    const skeletonView = pendingView ?? viewMode
    return skeletonView === "list" ? <ListSkeleton /> : <GridSkeleton />
  }

  if (isEmpty) {
    return <EmptyState whatsappNumber={whatsappNumber} />
  }

  return <>{children}</>
}
